import React, { useEffect, useMemo, useRef, useState } from 'react';
import { VenueAdsConfig } from '../types';

interface VenueAdOverlayProps {
  show: boolean;
  onClose: () => void;
  config: VenueAdsConfig;
  durationSeconds?: number;
}

export const VenueAdOverlay: React.FC<VenueAdOverlayProps> = ({ show, onClose, config, durationSeconds }) => {
  const enabledSources = useMemo(() => (config.sources || []).filter(s => s.enabled && s.url), [config.sources]);
  const [index, setIndex] = useState(0);
  const [countdown, setCountdown] = useState<number>(durationSeconds || config.defaultDurationSeconds || 20);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!show) return;
    setCountdown(durationSeconds || config.defaultDurationSeconds || 20);
    setIndex(0);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    intervalRef.current = window.setInterval(() => {
      setIndex(prev => {
        const total = Math.max(1, enabledSources.length);
        return (prev + 1) % total;
      });
    }, 6000) as unknown as number;
    return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [show, config.defaultDurationSeconds, durationSeconds, enabledSources.length]);

  useEffect(() => {
    if (!show) return;
    if (countdown <= 0) { onClose(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [show, countdown, onClose]);

  if (!show) return null;

  const src = enabledSources.length > 0 ? enabledSources[index] : undefined;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{
      backgroundColor: config.placement === 'full-screen' ? 'rgba(0,0,0,0.85)' : 'transparent'
    }}>
      <div className={config.placement === 'full-screen' ? 'w-full max-w-5xl' : 'absolute bottom-8 left-1/2 -translate-x-1/2'}>
        <div className={config.placement === 'full-screen' ? 'bg-black rounded-xl overflow-hidden shadow-2xl' : 'bg-black/80 rounded-xl overflow-hidden shadow-2xl'}>
          <div className="relative">
            {src ? (
              src.type === 'image' ? (
                <img src={src.url} alt={src.label || 'Venue Ad'} className={config.placement === 'full-screen' ? 'w-full max-h-[70vh] object-contain bg-black' : 'w-[80vw] max-w-3xl max-h-[240px] object-contain bg-black'} />
              ) : (
                <video src={src.url} autoPlay muted playsInline loop className={config.placement === 'full-screen' ? 'w-full max-h-[70vh] object-contain bg-black' : 'w-[80vw] max-w-3xl max-h-[240px] object-contain bg-black'} />
              )
            ) : (
              <div className="p-12 text-center text-white">
                <div className="text-5xl mb-2">🪧</div>
                <p>Venue Ad</p>
              </div>
            )}
            <button onClick={onClose} className="absolute top-2 right-2 text-white text-2xl font-bold">×</button>
          </div>
          <div className="p-3 text-white text-center text-sm bg-gray-900 border-t border-gray-800">
            Showing venue ad • closes in {countdown}s
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueAdOverlay;
